import { X } from "lucide-react";
import { Badge, cn } from "@fox1ck-org/ui";
import { getLevelStyle, shortenPod } from "./levelStyles";

interface LogsFilterChipsProps {
  pod: string;
  container: string;
  levels: string[];
  search: string;
  correlationId: string;
  onClearPod: () => void;
  onClearContainer: () => void;
  onRemoveLevel: (level: string) => void;
  onClearSearch: () => void;
  onClearCorrelation: () => void;
  onClearAll: () => void;
}

/**
 * Renders the active-filter strip below the toolbar. Each chip removes its
 * own filter; "Сбросить все" wipes them in one click.
 */
export function LogsFilterChips({
  pod,
  container,
  levels,
  search,
  correlationId,
  onClearPod,
  onClearContainer,
  onRemoveLevel,
  onClearSearch,
  onClearCorrelation,
  onClearAll,
}: LogsFilterChipsProps) {
  const hasAny = pod || container || levels.length > 0 || search || correlationId;
  if (!hasAny) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px]">
      <span className="text-muted-foreground">Фильтры:</span>

      {levels.map((level) => {
        const style = getLevelStyle(level);
        return (
          <Chip key={level} className={cn("border", style.pill)} onRemove={() => onRemoveLevel(level)}>
            {level}
          </Chip>
        );
      })}

      {pod && (
        <Chip onRemove={onClearPod}>
          pod=<span className="font-mono">{shortenPod(pod)}</span>
        </Chip>
      )}

      {container && (
        <Chip onRemove={onClearContainer}>
          container=<span className="font-mono">{container}</span>
        </Chip>
      )}

      {search && (
        <Chip onRemove={onClearSearch}>
          “<span className="font-mono">{search}</span>”
        </Chip>
      )}

      {correlationId && (
        <Chip onRemove={onClearCorrelation} className="text-primary border-primary/40">
          trace=<span className="font-mono">{correlationId.slice(0, 8)}…</span>
        </Chip>
      )}

      <button
        type="button"
        onClick={onClearAll}
        className="text-muted-foreground hover:text-foreground underline underline-offset-2"
      >
        Сбросить все
      </button>
    </div>
  );
}

function Chip({
  children,
  onRemove,
  className,
}: {
  children: React.ReactNode;
  onRemove: () => void;
  className?: string;
}) {
  return (
    <Badge variant="outline" className={cn("gap-1 h-5 text-[10px]", className)}>
      {children}
      <button
        type="button"
        onClick={onRemove}
        className="hover:opacity-70 -mr-0.5"
        aria-label="Снять фильтр"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </Badge>
  );
}
